import { LinkingOptions } from '@react-navigation/native';
import { Platform } from 'react-native';

/**
 * Deep linking configuration for React Native Web
 * Maps URL paths to navigation screens
 *
 * URL Structure:
 * - /                          -> Dashboard (org_employee) or Strategy Dashboard (strategy_team)
 * - /dashboard                 -> Dashboard
 * - /strategy                  -> Strategy Dashboard
 * - /goals                     -> Goals list
 * - /goals/:id                 -> Goal detail
 * - /initiatives               -> Initiatives list
 * - /initiatives/:id           -> Initiative detail
 * - /unit-goals                -> Unit Goals list
 * - /processes                 -> Processes list
 * - /services                  -> Services list
 * - /risks                     -> Risks list
 * - /export                    -> Export/Reports
 * - /admin                     -> Admin panel
 * - /notifications             -> Notifications
 * - /profile                   -> User profile
 * - /settings                  -> Settings
 */

// Type for the full navigation tree (matches Drawer navigator screens)
type RootParamList = {
  StrategyStack: {
    StrategyDashboard: undefined;
    OrganizationsList: undefined;
    OrganizationDetail: { id: number };
    PendingApprovals: undefined;
    PerformanceDeviation: undefined;
    Goals: { organizationId?: number };
    GoalDetail: { id: number };
    AddGoal: { goalId?: number; organizationId?: number };
    Initiatives: { goalId?: number; organizationId?: number };
    InitiativeDetail: { id: number };
    AddInitiative: { goalId?: number; initiativeId?: number };
    ActivityDetail: { id: number };
    AddActivity: { initiativeId: number; activityId?: number };
    ServiceDetail: { id: number };
    AddService: { goalId?: number; serviceId?: number };
    ProcessDetail: { id: number };
    AddProcess: { processId?: number; goalId?: number };
    SubProcessDetail: { id: number };
    AddSubProcess: { processId?: number; subProcessId?: number };
    UnitGoalDetail: { id: number };
    AddUnitGoal: { unitGoalId?: number; strategicGoalId?: number; unitId?: number };
    EmployeeDetail: { id: number };
    AddEmployee: { employeeId?: number; unitId?: number };
    KPIs: { organizationId?: number; goalId?: number; initiativeId?: number };
    KPIDetail: { id: number };
    AddKPIReading: { kpiId: number };
    Schedules: { organizationId?: number };
    // List screens shared with their root-level stacks; when navigated to
    // from OrganizationDetail, the org-scoped params are required for the
    // destination screen to filter by department.
    Processes: { organizationId?: number; organizationName?: string };
    Services: { organizationId?: number; organizationName?: string };
    UnitGoals: { organizationId?: number; organizationName?: string };
    Employees: { organizationId?: number; organizationName?: string };
  };
  DashboardStack: {
    Dashboard: { organizationId?: number } | undefined;
    EditOrganization: { organizationId: number };
    Employees: { organizationId?: number; organizationName?: string } | undefined;
    EmployeeDetail: { id: number };
    AddEmployee: { employeeId?: number; unitId?: number };
    UnitGoals: { organizationId?: number; organizationName?: string } | undefined;
    UnitGoalDetail: { id: number };
    AddUnitGoal: { unitGoalId?: number; strategicGoalId?: number };
    Services: { organizationId?: number; organizationName?: string } | undefined;
    ServiceDetail: { id: number };
    AddService: { goalId?: number; serviceId?: number };
    Processes: { organizationId?: number; organizationName?: string } | undefined;
    ProcessDetail: { id: number };
    AddProcess: { processId?: number };
    SubProcessDetail: { id: number };
    AddSubProcess: { processId?: number; subProcessId?: number };
    KPIDetail: { id: number };
    AddKPIReading: { kpiId: number };
    Goals: { organizationId?: number; organizationName?: string } | undefined;
    GoalDetail: { id: number };
    AddGoal: { goalId?: number; organizationId?: number };
    Initiatives: { organizationId?: number; organizationName?: string } | undefined;
    InitiativeDetail: { id: number };
    AddInitiative: { goalId?: number; initiativeId?: number };
    ActivityDetail: { id: number };
    AddActivity: { initiativeId: number; activityId?: number };
    KPIs: { organizationId?: number; organizationName?: string; goalId?: number; initiativeId?: number } | undefined;
    AddInitiativeKPI: { kpiId?: number; initiativeId?: number };
  };
  MyOrganizationStack: {
    MyOrganization: undefined;
    EditOrganization: { organizationId: number };
    AddOrganizationalUnit: { unitId?: number; parentId?: number };
    OrganizationalUnitDetail: { id: number };
    AddEmployee: { employeeId?: number; unitId?: number };
    EmployeeDetail: { id: number };
    AddGoal: { goalId?: number; organizationId?: number };
    GoalDetail: { id: number };
    InitiativeDetail: { id: number };
    AddInitiative: { goalId?: number; initiativeId?: number };
    ActivityDetail: { id: number };
    AddActivity: { initiativeId: number; activityId?: number };
    ServiceDetail: { id: number };
    AddService: { goalId?: number; serviceId?: number };
    ProcessDetail: { id: number };
    AddProcess: { processId?: number; goalId?: number };
    SubProcessDetail: { id: number };
    AddSubProcess: { processId?: number; subProcessId?: number };
    UnitGoalDetail: { id: number };
    AddUnitGoal: { unitGoalId?: number; strategicGoalId?: number; unitId?: number };
    KPIDetail: { id: number };
    AddKPIReading: { kpiId: number };
  };
  GoalsStack: {
    Goals: { organizationId?: number };
    GoalDetail: { id: number };
    AddGoal: { goalId?: number; organizationId?: number };
    InitiativeDetail: { id: number };
    AddInitiative: { goalId?: number; initiativeId?: number };
    ActivityDetail: { id: number };
    AddActivity: { initiativeId: number; activityId?: number };
    AddService: { goalId?: number; serviceId?: number };
    ServiceDetail: { id: number };
    AddProcess: { processId?: number; goalId?: number };
    ProcessDetail: { id: number };
    SubProcessDetail: { id: number };
    AddSubProcess: { processId?: number; subProcessId?: number };
    KPIDetail: { id: number };
    AddKPIReading: { kpiId: number };
    AddUnitGoal: { unitGoalId?: number; strategicGoalId?: number; unitId?: number };
    UnitGoalDetail: { id: number };
  };
  InitiativesStack: {
    Initiatives: { goalId?: number; organizationId?: number };
    InitiativeDetail: { id: number };
    AddInitiative: { goalId?: number; initiativeId?: number };
    AddInitiativeKPI: { initiativeId: number };
    AddActivity: { initiativeId: number; activityId?: number };
    ActivityDetail: { id: number };
    KPIDetail: { id: number };
    AddKPIReading: { kpiId: number };
  };
  UnitGoalsStack: {
    UnitGoals: undefined;
    UnitGoalDetail: { id: number };
    AddUnitGoal: { unitGoalId?: number; strategicGoalId?: number; unitId?: number };
    ImportUnitGoals: undefined;
    KPIDetail: { id: number };
    AddKPIReading: { kpiId: number };
  };
  ProcessesStack: {
    Processes: undefined;
    ProcessDetail: { id: number };
    SubProcessDetail: { id: number };
    AddProcess: { processId?: number; goalId?: number };
    AddSubProcess: { processId?: number; subProcessId?: number };
    ImportProcesses: undefined;
    GoalDetail: { id: number };
    AddGoal: { goalId?: number; organizationId?: number };
    InitiativeDetail: { id: number };
    AddInitiative: { goalId?: number; initiativeId?: number };
    ActivityDetail: { id: number };
    AddActivity: { initiativeId: number; activityId?: number };
    ServiceDetail: { id: number };
    AddService: { goalId?: number; serviceId?: number };
    UnitGoalDetail: { id: number };
    AddUnitGoal: { unitGoalId?: number; strategicGoalId?: number; unitId?: number };
    KPIDetail: { id: number };
    AddKPIReading: { kpiId: number };
  };
  ServicesStack: {
    Services: undefined;
    AddService: { goalId?: number; serviceId?: number };
    ServiceDetail: { id: number };
    KPIDetail: { id: number };
    AddKPIReading: { kpiId: number };
  };
  EvaluationStack: {
    EvaluationDashboard: undefined;
    EvaluationDetail: { assignmentId: number };
    CriteriaScoringForm: { assignmentId: number; axisId: number; criteriaIndex?: number };
    EvaluationReadOnly: { evaluationId: number; axisId?: number };
    AnnualEvalTable: { departmentId: number };
    MonthlyEvalTable: { departmentId: number };
    EvaluationManagement: undefined;
    FrameworkBuilder: { frameworkId?: number };
  };
  RisksStack: {
    Risks: { successMessage?: string };
    AddRisk: { riskId?: number; relatedType?: string; relatedId?: number };
  };
  ExportStack: {
    Export: undefined;
  };
  HelpStack: {
    Help: undefined;
  };
  DocumentsStack: {
    Documents: undefined;
    AddDocument: { documentId?: number } | undefined;
  };
  OrganizationsStack: {
    Organizations: undefined;
    OrganizationDetail: { id: number };
  };
  KPIsStack: {
    KPIs: { organizationId?: number; goalId?: number; initiativeId?: number };
    KPIDetail: { id: number };
    AddKPIReading: { kpiId: number };
  };
  SchedulesStack: {
    Schedules: { organizationId?: number };
  };
  NotificationsStack: {
    Notifications: undefined;
  };
  ProfileStack: {
    Profile: undefined;
  };
  SettingsStack: {
    Settings: undefined;
  };
  StrategyOrganizationsStack: {
    StrategyOrganizations: undefined;
    OrganizationDetail: { id: number };
    Goals: { organizationId?: number };
    GoalDetail: { id: number };
    AddGoal: { goalId?: number; organizationId?: number };
    Initiatives: { goalId?: number; organizationId?: number };
    InitiativeDetail: { id: number };
    AddInitiative: { goalId?: number; initiativeId?: number };
    ActivityDetail: { id: number };
    AddActivity: { initiativeId: number; activityId?: number };
    ServiceDetail: { id: number };
    AddService: { goalId?: number; serviceId?: number };
    ProcessDetail: { id: number };
    AddProcess: { processId?: number; goalId?: number };
    SubProcessDetail: { id: number };
    AddSubProcess: { processId?: number; subProcessId?: number };
    UnitGoalDetail: { id: number };
    AddUnitGoal: { unitGoalId?: number; strategicGoalId?: number; unitId?: number };
    EmployeeDetail: { id: number };
    AddEmployee: { employeeId?: number; unitId?: number };
    KPIs: { organizationId?: number; goalId?: number; initiativeId?: number };
    KPIDetail: { id: number };
    AddKPIReading: { kpiId: number };
  };
  SearchStack: {
    SearchResults: { query?: string };
    GoalDetail: { id: number };
    AddGoal: { goalId?: number; organizationId?: number };
    InitiativeDetail: { id: number };
    AddInitiative: { goalId?: number; initiativeId?: number };
    ActivityDetail: { id: number };
    AddActivity: { initiativeId: number; activityId?: number };
    KPIDetail: { id: number };
    AddKPIReading: { kpiId: number };
    ProcessDetail: { id: number };
    AddProcess: { processId?: number; goalId?: number };
    SubProcessDetail: { id: number };
    AddSubProcess: { processId?: number; subProcessId?: number };
    ServiceDetail: { id: number };
    AddService: { goalId?: number; serviceId?: number };
    UnitGoalDetail: { id: number };
    AddUnitGoal: { unitGoalId?: number; strategicGoalId?: number; unitId?: number };
  };
  EmployeesStack: {
    Employees: { organizationId?: number };
    EmployeeDetail: { id: number };
    AddEmployee: { employeeId?: number; unitId?: number };
    ImportEmployees: undefined;
    KPIDetail: { id: number };
    AddKPIReading: { kpiId: number };
  };
};

// Helper to parse numeric params
const parseNumber = (value: string | undefined): number | undefined => {
  if (!value) return undefined;
  const parsed = parseInt(value, 10);
  return isNaN(parsed) ? undefined : parsed;
};

export const linking: LinkingOptions<any> = {
  prefixes: Platform.OS === 'web'
    ? [
        typeof window !== 'undefined' ? window.location.origin : 'https://sms.adaadev.ae',
        'https://sms.adaadev.ae',
        'http://localhost:19006',
        'http://localhost:3002',
      ]
    : ['sms://'],

  config: {
    screens: {
      // Strategy Team Dashboard Stack
      StrategyStack: {
        path: 'strategy',
        screens: {
          StrategyDashboard: '',
          OrganizationsList: 'organizations-list',
          OrganizationDetail: 'organization/:id',
          PendingApprovals: 'pending-approvals',
          PerformanceDeviation: 'performance-deviation',
          Goals: 'goals',
          GoalDetail: 'goal/:id',
          AddGoal: 'add-goal',
          Initiatives: 'initiatives',
          InitiativeDetail: 'initiative/:id',
          AddInitiative: 'add-initiative',
          ActivityDetail: 'activity/:id',
          AddActivity: 'add-activity',
          ServiceDetail: 'service/:id',
          AddService: 'add-service',
          ProcessDetail: 'process/:id',
          AddProcess: 'add-process',
          SubProcessDetail: 'sub-process/:id',
          AddSubProcess: 'add-sub-process',
          UnitGoalDetail: 'unit-goal/:id',
          AddUnitGoal: 'add-unit-goal',
          EmployeeDetail: 'employee/:id',
          AddEmployee: 'add-employee',
          KPIs: 'kpis',
          KPIDetail: 'kpi/:id',
          AddKPIReading: 'kpi/:kpiId/add-reading',
          Schedules: 'schedules',
          // Drawer's "إدارة جداول المؤشرات" navigates to SchedulesManagement.
          // Without a URL mapping, hard-refresh on the management view loses
          // its drawer highlight because React Navigation can't restore it
          // from the URL alone.
          SchedulesManagement: 'schedules-management',
          // List screens reached from OrganizationDetail's "العمليات /
          // الخدمات / أهداف الوحدات / رأس المال البشري" cards. These screens
          // are *registered* in StrategyStack (MainNavigator) and accept an
          // organizationId param. Without the linking entry below, web
          // navigation falls back to the global root-level stacks
          // (ProcessesStack/ServicesStack/UnitGoalsStack/EmployeesStack),
          // which strip the org context and render unfiltered or blank.
          Processes: 'processes',
          Services: 'services',
          UnitGoals: 'unit-goals',
          Employees: 'employees',
        },
      },
      // Dashboard Stack (org_employee home)
      DashboardStack: {
        path: 'dashboard',
        screens: {
          // Optional :organizationId lets strategy_team / executive land on
          // the org-employee-style dashboard for any org via /dashboard/:id.
          // org_employee keeps the paramless /dashboard URL.
          Dashboard: ':organizationId?',
          EditOrganization: 'edit-organization/:organizationId',
          // Lists reachable from the dashboard's "View Details" cards —
          // kept inside DashboardStack so Back returns to /dashboard/:id.
          Employees: 'employees',
          EmployeeDetail: 'employee/:id',
          AddEmployee: 'add-employee',
          UnitGoals: 'unit-goals',
          UnitGoalDetail: 'unit-goal/:id',
          AddUnitGoal: 'add-unit-goal',
          Services: 'services',
          ServiceDetail: 'service/:id',
          AddService: 'add-service',
          Processes: 'processes',
          ProcessDetail: 'process/:id',
          AddProcess: 'add-process',
          SubProcessDetail: 'sub-process/:id',
          AddSubProcess: 'add-sub-process',
          KPIDetail: 'kpi/:id',
          AddKPIReading: 'kpi/:kpiId/add-reading',
          // Top-level stat cards (goals / initiatives / KPIs) also live in
          // DashboardStack so back-nav stays in /dashboard/:id context.
          Goals: 'goals',
          GoalDetail: 'goal/:id',
          AddGoal: 'add-goal',
          Initiatives: 'initiatives',
          InitiativeDetail: 'initiative/:id',
          AddInitiative: 'add-initiative',
          ActivityDetail: 'activity/:id',
          AddActivity: 'add-activity',
          KPIs: 'kpis',
          AddInitiativeKPI: 'add-initiative-kpi',
        },
      },
      // My Organization Stack
      MyOrganizationStack: {
        path: 'my-organization',
        screens: {
          MyOrganization: '',
          EditOrganization: 'edit/:organizationId',
          AddOrganizationalUnit: 'add-unit',
          OrganizationalUnitDetail: 'unit/:id',
          AddEmployee: 'add-employee',
          EmployeeDetail: 'employee/:id',
          AddGoal: 'add-goal',
          GoalDetail: 'goal/:id',
          InitiativeDetail: 'initiative/:id',
          AddInitiative: 'add-initiative',
          ActivityDetail: 'activity/:id',
          AddActivity: 'add-activity',
          ServiceDetail: 'service/:id',
          AddService: 'add-service',
          ProcessDetail: 'process/:id',
          AddProcess: 'add-process',
          SubProcessDetail: 'sub-process/:id',
          AddSubProcess: 'add-sub-process',
          UnitGoalDetail: 'unit-goal/:id',
          AddUnitGoal: 'add-unit-goal',
          KPIDetail: 'kpi/:id',
          AddKPIReading: 'kpi/:kpiId/reading',
        },
      },
      // Goals Stack
      GoalsStack: {
        path: 'goals',
        screens: {
          Goals: '',
          GoalDetail: ':id',
          AddGoal: 'add',
          InitiativeDetail: 'initiative/:id',
          AddInitiative: 'add-initiative',
          ActivityDetail: 'activity/:id',
          AddActivity: 'add-activity',
          AddService: 'add-service',
          ServiceDetail: 'service/:id',
          AddProcess: 'add-process',
          ProcessDetail: 'process/:id',
          SubProcessDetail: 'sub-process/:id',
          AddSubProcess: 'add-sub-process',
          KPIDetail: 'kpi/:id',
          AddKPIReading: 'kpi/:kpiId/reading',
          AddUnitGoal: 'add-unit-goal',
          UnitGoalDetail: 'unit-goal/:id',
        },
      },
      // Initiatives Stack
      InitiativesStack: {
        path: 'initiatives',
        screens: {
          Initiatives: '',
          InitiativeDetail: ':id',
          AddInitiative: 'add',
          AddInitiativeKPI: ':initiativeId/add-kpi',
          AddActivity: ':initiativeId/add-activity',
          ActivityDetail: 'activity/:id',
          KPIDetail: 'kpi/:id',
          AddKPIReading: 'kpi/:kpiId/reading',
        },
      },
      // Unit Goals Stack
      UnitGoalsStack: {
        path: 'unit-goals',
        screens: {
          UnitGoals: '',
          UnitGoalDetail: ':id',
          AddUnitGoal: 'add',
          ImportUnitGoals: 'import',
          KPIDetail: 'kpi/:id',
          AddKPIReading: 'kpi/:kpiId/reading',
        },
      },
      // Processes Stack
      ProcessesStack: {
        path: 'processes',
        screens: {
          Processes: '',
          ProcessDetail: ':id',
          SubProcessDetail: 'sub-process/:id',
          AddProcess: 'add',
          AddSubProcess: ':processId/add-sub-process',
          ImportProcesses: 'import',
          GoalDetail: 'goal/:id',
          AddGoal: 'add-goal',
          InitiativeDetail: 'initiative/:id',
          AddInitiative: 'add-initiative',
          ActivityDetail: 'activity/:id',
          AddActivity: 'add-activity',
          ServiceDetail: 'service/:id',
          AddService: 'add-service',
          UnitGoalDetail: 'unit-goal/:id',
          AddUnitGoal: 'add-unit-goal',
          KPIDetail: 'kpi/:id',
          AddKPIReading: 'kpi/:kpiId/reading',
        },
      },
      // Services Stack
      ServicesStack: {
        path: 'services',
        screens: {
          Services: '',
          AddService: 'add',
          ServiceDetail: ':id',
          KPIDetail: 'kpi/:id',
          AddKPIReading: 'kpi/:kpiId/reading',
        },
      },
      // Evaluation Stack
      EvaluationStack: {
        path: 'evaluation',
        screens: {
          EvaluationDashboard: '',
          EvaluationDetail: 'assignment/:assignmentId',
          CriteriaScoringForm: 'assignment/:assignmentId/axis/:axisId',
          EvaluationReadOnly: 'view/:evaluationId',
          AnnualEvalTable: 'annual/:departmentId',
          MonthlyEvalTable: 'monthly/:departmentId',
          EvaluationManagement: 'manage',
          FrameworkBuilder: 'framework/build',
        },
      },
      RisksStack: {
        path: 'risks',
        screens: {
          Risks: '',
          AddRisk: 'add',
        },
      },
      // Export Stack
      ExportStack: {
        path: 'export',
        screens: {
          Export: '',
        },
      },
      // Admin Stack removed 2026-05-13 — user management lives in DTS-SSO.
      // Help Stack
      HelpStack: {
        path: 'help',
        screens: {
          Help: '',
        },
      },
      // الدلائل والمرجعيات — authority-wide document library
      DocumentsStack: {
        path: 'documents',
        screens: {
          Documents: '',
          AddDocument: 'add',
        },
      },
      // Organizations Stack (hidden)
      OrganizationsStack: {
        path: 'organizations',
        screens: {
          Organizations: '',
          OrganizationDetail: ':id',
        },
      },
      // KPIs Stack removed - KPIs accessed through Goals/Initiatives stacks
      // Schedules Stack
      SchedulesStack: {
        path: 'schedules',
        screens: {
          Schedules: '',
        },
      },
      // Notifications Stack
      NotificationsStack: {
        path: 'notifications',
        screens: {
          Notifications: '',
        },
      },
      // Profile Stack
      ProfileStack: {
        path: 'profile',
        screens: {
          Profile: '',
        },
      },
      // Settings Stack
      SettingsStack: {
        path: 'settings',
        screens: {
          Settings: '',
        },
      },
      // Strategy Organizations Stack
      StrategyOrganizationsStack: {
        path: 'strategy-organizations',
        screens: {
          StrategyOrganizations: '',
          OrganizationDetail: 'organization/:id',
          Goals: 'goals',
          GoalDetail: 'goal/:id',
          AddGoal: 'add-goal',
          Initiatives: 'initiatives',
          InitiativeDetail: 'initiative/:id',
          AddInitiative: 'add-initiative',
          ActivityDetail: 'activity/:id',
          AddActivity: 'add-activity',
          ServiceDetail: 'service/:id',
          AddService: 'add-service',
          ProcessDetail: 'process/:id',
          AddProcess: 'add-process',
          SubProcessDetail: 'sub-process/:id',
          AddSubProcess: 'add-sub-process',
          UnitGoalDetail: 'unit-goal/:id',
          AddUnitGoal: 'add-unit-goal',
          EmployeeDetail: 'employee/:id',
          AddEmployee: 'add-employee',
          KPIs: 'kpis',
          KPIDetail: 'kpi/:id',
          AddKPIReading: 'kpi/:kpiId/reading',
        },
      },
      // Search Stack
      SearchStack: {
        path: 'search',
        screens: {
          SearchResults: '',
          GoalDetail: 'goal/:id',
          AddGoal: 'add-goal',
          InitiativeDetail: 'initiative/:id',
          AddInitiative: 'add-initiative',
          ActivityDetail: 'activity/:id',
          AddActivity: 'add-activity',
          KPIDetail: 'kpi/:id',
          AddKPIReading: 'kpi/:kpiId/reading',
          ProcessDetail: 'process/:id',
          AddProcess: 'add-process',
          SubProcessDetail: 'sub-process/:id',
          AddSubProcess: 'add-sub-process',
          ServiceDetail: 'service/:id',
          AddService: 'add-service',
          UnitGoalDetail: 'unit-goal/:id',
          AddUnitGoal: 'add-unit-goal',
        },
      },
      // Employees Stack
      EmployeesStack: {
        path: 'employees',
        screens: {
          Employees: '',
          EmployeeDetail: ':id',
          AddEmployee: 'add',
          ImportEmployees: 'import',
          KPIDetail: 'kpi/:id',
          AddKPIReading: 'kpi/:kpiId/reading',
        },
      },

    },
  },
};

export default linking;
